import React, { useMemo } from "react";
import {
  AccordionTriggerSlot,
  AccordionTitleSlot,
  AccordionHeaderRightContentSlot,
} from "../accordion.slots";
import { Flex } from "@/components";
import { KeyboardArrowRight } from "@commercetools/nimbus-icons";
import { Button as RaButton } from "react-aria-components";
import type { AccordionHeaderProps } from "../accordion.types";
import { extractStyleProps } from "@/utils";
import { AccordionHeaderRightContent } from "./accordion.header-right-content";

/**
 * Accordion.Header - The clickable header for accordion items
 *
 * Expands/collapses the accordion content when activated.
 * Handles keyboard and mouse interactions.
 *
 * @supportsStyleProps
 */
export const AccordionHeader = ({
  children,
  ref,
  ...props
}: AccordionHeaderProps) => {
  const [styleProps, restProps] = extractStyleProps(props);
  // Extract HeaderRightContent if present
  const headerContent = useMemo(() => {
    const main: React.ReactNode[] = [];
    const rightContent: React.ReactNode[] = [];

    React.Children.forEach(children, (child) => {
      if (
        React.isValidElement(child) &&
        (child.type === AccordionHeaderRightContent ||
          child.type === AccordionHeaderRightContentSlot)
      ) {
        rightContent.push(child);
      } else {
        main.push(child);
      }
    });

    return { main, rightContent };
  }, [children]);

  return (
    <Flex
      justifyContent="space-between"
      alignItems="center"
      borderBottom="solid-25"
      borderColor="neutral.4"
      {...styleProps}
    >
      <AccordionTriggerSlot ref={ref} slot="trigger" asChild>
        <RaButton {...restProps}>
          <KeyboardArrowRight />
          <AccordionTitleSlot>{headerContent.main}</AccordionTitleSlot>
        </RaButton>
      </AccordionTriggerSlot>
      {headerContent.rightContent.length > 0 && (
        <AccordionHeaderRightContentSlot>
          {headerContent.rightContent}
        </AccordionHeaderRightContentSlot>
      )}
    </Flex>
  );
};

AccordionHeader.displayName = "Accordion.Header";
